import React from 'react';
import SEO from '../components/SEO';
import AdUnit from '../components/AdUnit';

const WhatIsJsonPage = () => {
  return (
    <>
      <SEO 
        title="What is JSON? Complete Guide to JavaScript Object Notation" 
        description="Learn everything about JSON (JavaScript Object Notation) - the popular data format for web APIs, configuration files, and data exchange between systems."
        keywords={["what is JSON", "JavaScript Object Notation", "JSON format", "JSON syntax", "JSON data structure", "JSON examples", "JSON vs XML", "JSON tutorial"]}
        canonicalUrl="https://yourjsontools.com/what-is-json"
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', lineHeight: '1.6', color: '#333' }}>

        <h1 style={{ fontSize: '32px', marginBottom: '20px', color: '#2c3e50' }}>What is JSON?</h1>
        
        <p style={{ fontSize: '18px', marginBottom: '25px', color: '#555' }}>
          JSON (JavaScript Object Notation) is a lightweight, text-based data interchange format that's easy for humans to read and write, and easy for machines to parse and generate. Despite its name suggesting a connection to JavaScript, JSON is language-independent and widely used across different programming languages and platforms.
        </p>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>JSON Syntax and Structure</h2>
        
        <p style={{ marginBottom: '15px' }}>
          JSON is built on two main structures:
        </p>
        
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Objects:</strong> An unordered collection of key/value pairs enclosed in curly braces {}</li>
          <li style={{ marginBottom: '8px' }}><strong>Arrays:</strong> An ordered list of values enclosed in square brackets []</li>
        </ul>

        <h3 style={{ fontSize: '20px', marginTop: '30px', marginBottom: '10px', color: '#34495e' }}>JSON Data Types</h3>
        
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '6px' }}><strong>String:</strong> Text enclosed in double quotes</li>
          <li style={{ marginBottom: '6px' }}><strong>Number:</strong> Integer or floating point</li>
          <li style={{ marginBottom: '6px' }}><strong>Boolean:</strong> true or false</li>
          <li style={{ marginBottom: '6px' }}><strong>null:</strong> Represents empty value</li>
          <li style={{ marginBottom: '6px' }}><strong>Object:</strong> Collection of key/value pairs</li>
          <li style={{ marginBottom: '6px' }}><strong>Array:</strong> Ordered list of values</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>JSON Examples</h2>

        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>Simple JSON Object</h3>
        <pre style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #e9ecef', overflow: 'auto', marginBottom: '20px' }}>
{`{
  "name": "John Doe",
  "age": 30,
  "city": "New York",
  "isEmployed": true
}`}
        </pre>

        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>JSON Array</h3>
        <pre style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #e9ecef', overflow: 'auto', marginBottom: '20px' }}>
{`[
  "apple",
  "banana", 
  "cherry"
]`}
        </pre>

        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>Complex JSON Structure</h3>
        <pre style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #e9ecef', overflow: 'auto', marginBottom: '20px' }}>
{`{
  "users": [
    {
      "id": 1,
      "name": "Alice Johnson",
      "email": "alice@example.com",
      "preferences": {
        "theme": "dark",
        "notifications": true
      }
    },
    {
      "id": 2,
      "name": "Bob Smith", 
      "email": "bob@example.com",
      "preferences": {
        "theme": "light",
        "notifications": false
      }
    }
  ],
  "total": 2
}`}
        </pre>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>Why Use JSON?</h2>
        
        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>Advantages of JSON</h3>
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Lightweight:</strong> Less verbose than XML</li>
          <li style={{ marginBottom: '8px' }}><strong>Human-readable:</strong> Easy to read and understand</li>
          <li style={{ marginBottom: '8px' }}><strong>Language-independent:</strong> Supported by virtually all programming languages</li>
          <li style={{ marginBottom: '8px' }}><strong>Web-friendly:</strong> Native JavaScript support makes it perfect for web applications</li>
          <li style={{ marginBottom: '8px' }}><strong>Fast parsing:</strong> Quick to parse and generate</li>
          <li style={{ marginBottom: '8px' }}><strong>Widespread adoption:</strong> Industry standard for APIs and configuration files</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>Common Use Cases</h2>
        
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Web APIs:</strong> REST APIs commonly use JSON for request and response data</li>
          <li style={{ marginBottom: '8px' }}><strong>Configuration files:</strong> Many applications use JSON for configuration</li>
          <li style={{ marginBottom: '8px' }}><strong>Data storage:</strong> NoSQL databases like MongoDB store data in JSON-like formats</li>
          <li style={{ marginBottom: '8px' }}><strong>AJAX requests:</strong> Web applications use JSON for asynchronous data exchange</li>
          <li style={{ marginBottom: '8px' }}><strong>Data transmission:</strong> Sending data between servers and web applications</li>
          <li style={{ marginBottom: '8px' }}><strong>Package manifests:</strong> Files like package.json in Node.js projects</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>JSON vs Other Formats</h2>

        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>JSON vs XML</h3>
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '6px' }}>JSON is more compact and less verbose</li>
          <li style={{ marginBottom: '6px' }}>JSON has better JavaScript integration</li>
          <li style={{ marginBottom: '6px' }}>XML supports attributes and namespaces</li>
          <li style={{ marginBottom: '6px' }}>XML has better schema validation support</li>
        </ul>

        <h3 style={{ fontSize: '20px', marginTop: '25px', marginBottom: '10px', color: '#34495e' }}>JSON vs YAML</h3>
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '6px' }}>YAML is more human-readable</li>
          <li style={{ marginBottom: '6px' }}>JSON has better performance</li>
          <li style={{ marginBottom: '6px' }}>JSON has wider language support</li>
          <li style={{ marginBottom: '6px' }}>YAML supports comments, JSON doesn't</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>JSON Best Practices</h2>
        
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Use double quotes:</strong> Always use double quotes for strings, not single quotes</li>
          <li style={{ marginBottom: '8px' }}><strong>Validate JSON:</strong> Always validate JSON before using it in production</li>
          <li style={{ marginBottom: '8px' }}><strong>Use consistent naming:</strong> Choose camelCase or snake_case and stick to it</li>
          <li style={{ marginBottom: '8px' }}><strong>Avoid deep nesting:</strong> Keep structure reasonably flat for better performance</li>
          <li style={{ marginBottom: '8px' }}><strong>Use meaningful names:</strong> Choose descriptive key names</li>
          <li style={{ marginBottom: '8px' }}><strong>Handle errors:</strong> Always include error handling when parsing JSON</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>Working with JSON</h2>
        
        <p style={{ marginBottom: '15px' }}>
          Most programming languages provide built-in support for JSON parsing and generation:
        </p>
        
        <ul style={{ marginBottom: '20px', paddingLeft: '25px' }}>
          <li style={{ marginBottom: '6px' }}><strong>JavaScript:</strong> JSON.parse() and JSON.stringify()</li>
          <li style={{ marginBottom: '6px' }}><strong>Python:</strong> json module with dumps() and loads()</li>
          <li style={{ marginBottom: '6px' }}><strong>Java:</strong> Libraries like Jackson or Gson</li>
          <li style={{ marginBottom: '6px' }}><strong>C#:</strong> System.Text.Json or Newtonsoft.Json</li>
          <li style={{ marginBottom: '6px' }}><strong>PHP:</strong> json_encode() and json_decode()</li>
        </ul>

        <h2 style={{ fontSize: '24px', marginTop: '40px', marginBottom: '15px', color: '#2c3e50' }}>Conclusion</h2>
        
        <p style={{ marginBottom: '20px' }}>
          JSON has become the de facto standard for data exchange in modern web development due to its simplicity, readability, and wide support across programming languages. Whether you're building REST APIs, configuring applications, or storing data, understanding JSON is essential for modern software development.
        </p>

        <p style={{ marginBottom: '30px' }}>
          Its lightweight nature and human-readable format make it an excellent choice for most data interchange scenarios, though the specific requirements of your project should always guide your choice of data format.
        </p>

      </div>
    </>
  );
};

export default WhatIsJsonPage; 